/**
 * OptimizedAtlasLoader - Loads pre-built character atlases with runtime fallback
 *
 * Checks for pre-built atlas.webp + atlas.json files and loads them directly.
 * Falls back to SpriteAtlasBuilder if no pre-built atlas is available.
 */
import SpriteAtlasBuilder, { SpriteAtlas } from './SpriteAtlasBuilder.js';
import Debug from '../utils/Debug.js';

export default class OptimizedAtlasLoader {
  /**
   * Load atlas for a character, preferring pre-built if available
   * @param {string} basePath - Character base path (e.g., '/characters/mage')
   * @param {CharacterMetadata} metadata - Full metadata for fallback
   * @returns {Promise<SpriteAtlas>}
   */
  static async load(basePath, metadata) {
    const atlasWebpUrl = `${basePath}/atlas.webp`;
    const atlasJsonUrl = `${basePath}/atlas.json`;

    try {
      // Check if pre-built atlas exists using parallel requests
      const [webpResponse, jsonResponse] = await Promise.all([
        fetch(atlasWebpUrl, { method: 'HEAD' }),
        fetch(atlasJsonUrl)
      ]);

      if (webpResponse.ok && jsonResponse.ok) {
        const atlasData = await jsonResponse.json();
        Debug.log('AtlasLoader', `Loading pre-built atlas for ${basePath}`);
        return this.createFromOptimized(basePath, atlasData, metadata);
      }
    } catch (e) {
      // Pre-built atlas not available, fall back to runtime building
    }

    Debug.log('AtlasLoader', `Pre-built atlas not found for ${basePath}, building at runtime...`);
    return SpriteAtlasBuilder.build(metadata);
  }

  /**
   * Create a SpriteAtlas-compatible object from optimized atlas data
   * @param {string} basePath - Character base path
   * @param {Object} atlasData - Parsed atlas.json data
   * @param {CharacterMetadata} metadata - Character metadata
   * @returns {SpriteAtlas}
   */
  static createFromOptimized(basePath, atlasData, metadata) {
    const { atlas, rotations, animations } = atlasData;

    // Build animation lookup table (matches SpriteAtlas format)
    const animationLookup = new Map();
    for (const [animName, directions] of Object.entries(animations)) {
      const directionMap = new Map();
      for (const [direction, frames] of Object.entries(directions)) {
        const framePositions = frames.map(pos => ({
          x: pos.x,
          y: pos.y,
          bgPosition: `-${pos.x}px -${pos.y}px`
        }));
        directionMap.set(direction, framePositions);
      }
      animationLookup.set(animName, directionMap);
    }

    // Build rotation lookup table
    const rotationLookup = new Map();
    for (const [direction, pos] of Object.entries(rotations)) {
      rotationLookup.set(direction, {
        x: pos.x,
        y: pos.y,
        bgPosition: `-${pos.x}px -${pos.y}px`
      });
    }

    // Create a SpriteAtlas-compatible object
    return new PrebuiltSpriteAtlas({
      url: `${basePath}/atlas.webp`,
      width: atlas.width,
      height: atlas.height,
      frameWidth: atlas.frameWidth,
      frameHeight: atlas.frameHeight,
      cols: atlas.cols,
      rows: atlas.rows,
      animationLookup,
      rotationLookup,
      metadata,
      isPrebuilt: true
    });
  }
}

/**
 * PrebuiltSpriteAtlas - Compatible with SpriteAtlas but loaded from pre-built files
 */
class PrebuiltSpriteAtlas {
  constructor(options) {
    this.url = options.url;
    this.width = options.width;
    this.height = options.height;
    this.frameWidth = options.frameWidth;
    this.frameHeight = options.frameHeight;
    this.cols = options.cols;
    this.rows = options.rows;
    this.animationLookup = options.animationLookup;
    this.rotationLookup = options.rotationLookup;
    this.metadata = options.metadata;
    this.isPrebuilt = options.isPrebuilt || false;

    // Create an empty frameMap for compatibility
    this.frameMap = new Map();
  }

  /**
   * Get frame position for an animation frame
   * @param {string} animName - Animation name
   * @param {string} direction - Direction
   * @param {number} frameIndex - Frame index
   * @returns {{x: number, y: number, bgPosition: string}|null}
   */
  getAnimationFrame(animName, direction, frameIndex) {
    const directionMap = this.animationLookup.get(animName);
    if (!directionMap) return null;

    const frames = directionMap.get(direction);
    if (!frames || frameIndex < 0 || frameIndex >= frames.length) return null;

    return frames[frameIndex];
  }

  /**
   * Get frame count for an animation in a direction
   * @param {string} animName - Animation name
   * @param {string} direction - Direction
   * @returns {number}
   */
  getFrameCount(animName, direction) {
    const directionMap = this.animationLookup.get(animName);
    if (!directionMap) return 0;

    const frames = directionMap.get(direction);
    return frames ? frames.length : 0;
  }

  /**
   * Get frame position for a static rotation
   * @param {string} direction - Direction
   * @returns {{x: number, y: number, bgPosition: string}|null}
   */
  getRotationFrame(direction) {
    return this.rotationLookup.get(direction) || null;
  }

  /**
   * Get CSS properties for using this atlas
   * @returns {Object} CSS properties object
   */
  getCSSProperties() {
    return {
      backgroundImage: `url(${this.url})`,
      backgroundSize: `${this.width}px ${this.height}px`,
      width: `${this.frameWidth}px`,
      height: `${this.frameHeight}px`,
      imageRendering: 'pixelated'
    };
  }

  /**
   * Clean up resources
   * For pre-built atlases, there's no blob URL to revoke
   */
  destroy() {
    this.animationLookup.clear();
    this.rotationLookup.clear();
    this.frameMap.clear();
  }
}
